/**
 * The two markups MyLib writes, named once for the whole frontend.
 *
 * The service says the same thing in `core/markup/Markup.kt`, and both say it as **media types**,
 * because that is what a document's format already is — the media type of its file. A name of MyLib's
 * own would mean a translation table between the two, and a translation table is where a second markup
 * language goes wrong.
 *
 * Nothing here knows how to draw either of them: that is `preview/renderers.ts`, which looks a format
 * up and fetches what draws it. This module is the vocabulary, so that a control offering the choice
 * and a lookup answering it are talking about the same strings.
 */

export const MARKDOWN = 'text/markdown'

export const ASCIIDOC = 'text/asciidoc'

/** What a text is written in when nothing says otherwise. */
export const DEFAULT_MARKUP = MARKDOWN

/**
 * What *Create …* in the library makes, and therefore what the combo button's press says.
 *
 * AsciiDoc rather than Markdown, because a document written in the library is the long kind — the
 * one with cross-references, footnotes and a table that has a header. What is short enough for
 * Markdown is usually a note, and the notepad has its own answer.
 */
export const MARKUP_FOR_A_NEW_DOCUMENT = ASCIIDOC

/** The media type without its parameters and in one case, so `text/markdown; charset=utf-8` matches. */
export function bare(format: string): string {
  return format.split(';')[0]?.trim().toLowerCase() ?? ''
}

/** What each markup is called where somebody reads it. */
export function nameOfMarkup(format: string): string {
  switch (bare(format)) {
    case MARKDOWN:
      return 'Markdown'
    case ASCIIDOC:
      return 'AsciiDoc'
    default:
      return 'Plain text'
  }
}

/**
 * Every markup MyLib writes, in the order a control offers them.
 *
 * **A third language is a line here.** `nameOfMarkup` gains a case, `preview/renderers.ts` gains a
 * renderer and the typesetter gains a reader — and no control anywhere counts to two.
 */
export const MARKUPS: readonly string[] = [MARKDOWN, ASCIIDOC]

/** Whether this is one MyLib writes — asked before a control offers to change it. */
export function isAMarkup(format: string): boolean {
  return MARKUPS.includes(bare(format))
}

/**
 * The markup the next new **note** is written in: the one last chosen on the pad.
 *
 * **Why a memory here and not in the library.** A note is made by typing into it — there is no control
 * in front of it that could offer a language, and putting one there would be the dialog the notepad
 * exists without. So the pad remembers, and switching afterwards costs one press either way.
 *
 * A document does not need this: it is made by a control that offers the languages itself, so nothing
 * has to be guessed from last time.
 *
 * It lives in this browser like the editor's other habits, and a browser that refuses storage simply
 * gets Markdown — a remembered choice is a convenience, and losing it must never be a broken editor.
 */
const KEPT = 'mylib.markup.chosen'

export function markupForSomethingNew(): string {
  try {
    const kept = window.localStorage.getItem(KEPT)
    return kept !== null && isAMarkup(kept) ? kept : DEFAULT_MARKUP
  } catch {
    return DEFAULT_MARKUP
  }
}

export function rememberMarkup(format: string): void {
  try {
    window.localStorage.setItem(KEPT, bare(format))
  } catch {
    // See above.
  }
}
